import { useEffect, useState } from "react";
import ContactType from "./contact-type";
import LeadSourcePage from "../lead-source/mainCreateLead";
import { useStateValue } from "../../context/StateProvider";
import MainUserManagement from "../user-managment/main-user";
import MainTaskTemplate from "../task-template/main-template";
import MainLockboxInventory from "../lockbox-inventory/main-lockbox";
import { useLocation } from "react-router-dom";
import {
  loadBrokerageSettings,
  isSuperAdminOwner,
} from "../../utils/brokerageSettings";
import EmailLog from "./email-log";
import EmailTemplatesTab from "./email-templates-tab";
import NotificationsTab from "./notifications-tab";
import LabelsTab from "./labels-tab";
import BulkUploadTab from "./bulk-upload-tab";
import TeamDefaultsTab from "./team-defaults-tab";
import CoordinatorsTab from "./coordinators-tab";
import IntegrationsTab from "./integrations-tab";
import SuperAdminTab from "./super-admin-tab";
import ImpactScorecardTab from "./impact-scorecard-tab";
import PortalFormsEditor from "./PortalFormsEditor";
import { isImpactScorecardEnabled } from "../../utils/featureFlags";
import PageHeader from "../PageHeader";

// Old top-level tabs that now live as sub-tabs under Integrations. Deep links
// (e.g. the contacts page's ?tab=gmailConnection) keep working via this map.
const LEGACY_SETTINGS_TAB_MAP = {
  gmailConnection: "gmail",
  followUpBoss: "followUpBoss",
  bombBomb: "bombBomb",
};

function MainSettings() {
  const [{ user }] = useStateValue();
  const location = useLocation();
  const queryTab = new URLSearchParams(location.search).get("tab");
  const [activeTab, setActiveTab] = useState("integrations");
  const [integrationsSub, setIntegrationsSub] = useState("gmail");
  const [superAdminAccess, setSuperAdminAccess] = useState(
    isSuperAdminOwner(user),
  );
  const tabs = [
    {
      id: "bulkUpload",
      label: "Bulk Upload Deals",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "labels",
      label: "Contact Labels",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "contactType",
      label: "Contact Type",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "emailLog",
      label: "Email Log",
      allowedUserRole: ["Admin", "Supervisor", "Employee"],
    },
    {
      id: "emailTemplates",
      label: "Email Templates",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "integrations",
      label: "Integrations",
      allowedUserRole: ["Admin", "Supervisor", "Employee"],
    },
    {
      id: "leadSource",
      label: "Lead Source",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "lockboxInventory",
      label: "Lockbox Inventory",
      allowedUserRole: ["Admin", "Supervisor", "Employee"],
    },
    {
      id: "notifications",
      label: "Notifications",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "portalForms",
      label: "Portal Forms",
      allowedUserRole: ["Admin"],
    },
    {
      id: "taskTemplate",
      label: "Task Templates",
      allowedUserRole: ["Admin", "Supervisor", "Employee"],
    },
    {
      id: "coordinators",
      label: "Coordinators",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "teamDefaults",
      label: "Team Defaults",
      allowedUserRole: ["Admin", "Supervisor"],
    },
    {
      id: "userManagment",
      label: "User Managment",
      allowedUserRole: ["Admin", "Supervisor"],
    },
  ].sort((a, b) => a.label.localeCompare(b.label));

  const userRole = user?.role;

  const filteredTabs = tabs
    .filter(
      (tab) =>
        Array.isArray(tab.allowedUserRole) &&
        tab.allowedUserRole.includes(userRole),
    )
    .concat(
      superAdminAccess ? [{ id: "superAdmin", label: "Super Admin" }] : [],
    )
    .concat(
      isImpactScorecardEnabled({ user })
        ? [{ id: "impactScorecard", label: "IMPACT Scorecard" }]
        : [],
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (!queryTab) return;
    // Old integration tab ids → the Integrations tab + right sub-tab.
    if (LEGACY_SETTINGS_TAB_MAP[queryTab]) {
      setActiveTab("integrations");
      setIntegrationsSub(LEGACY_SETTINGS_TAB_MAP[queryTab]);
      return;
    }
    if (filteredTabs.find((t) => t.id === queryTab)) {
      setActiveTab(queryTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryTab, superAdminAccess]);

  // Super Admin tab visibility: the owner always sees it; granted guests too.
  useEffect(() => {
    let cancelled = false;
    loadBrokerageSettings().then((s) => {
      if (cancelled) return;
      setSuperAdminAccess(
        isSuperAdminOwner(user) || (s.allowedUserIds || []).includes(user?.id),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <div className="h-screen flex flex-col bg-slate-50">
      <PageHeader title="Settings" />

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
        <nav
          className="
            flex-shrink-0
            flex flex-row lg:flex-col gap-1
            overflow-x-auto lg:overflow-x-visible lg:overflow-y-auto
            border-b lg:border-b-0 lg:border-r border-black/10
            bg-white
            px-3 py-2 lg:px-3 lg:py-4
            lg:w-[220px]
          "
        >
          {filteredTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`
                  group relative text-left whitespace-nowrap
                  text-sm font-medium
                  px-4 py-2.5 rounded-md flex-shrink-0
                  transition-colors
                  ${
                    isActive
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }
                `}
              >
                <span
                  className={`
                    hidden lg:block
                    absolute left-0 top-1/2 -translate-y-1/2
                    w-[3px] rounded-r-md
                    bg-indigo-500
                    transition-all duration-200
                    ${isActive ? "h-[60%]" : "h-0 group-hover:h-[40%]"}
                  `}
                />
                {tab.label}
              </button>
            );
          })}
        </nav>

        <div className="settings-dense flex-1 min-h-0 overflow-y-auto p-3 lg:p-4">
          {activeTab === "superAdmin" ? (
            <SuperAdminTab />
          ) : activeTab === "impactScorecard" ? (
            <ImpactScorecardTab />
          ) : activeTab === "contactType" ? (
            <ContactType />
          ) : activeTab === "leadSource" ? (
            <LeadSourcePage />
          ) : activeTab === "userManagment" ? (
            <MainUserManagement />
          ) : activeTab === "taskTemplate" ? (
            <MainTaskTemplate />
          ) : activeTab === "coordinators" ? (
            <CoordinatorsTab />
          ) : activeTab === "integrations" ? (
            <IntegrationsTab initialSub={integrationsSub} />
          ) : activeTab === "lockboxInventory" ? (
            <MainLockboxInventory />
          ) : activeTab === "emailTemplates" ? (
            <EmailTemplatesTab />
          ) : activeTab === "notifications" ? (
            <NotificationsTab />
          ) : activeTab === "portalForms" ? (
            <PortalFormsEditor />
          ) : activeTab === "labels" ? (
            <LabelsTab />
          ) : activeTab === "bulkUpload" ? (
            <BulkUploadTab />
          ) : activeTab === "teamDefaults" ? (
            <TeamDefaultsTab />
          ) : (
            <EmailLog />
          )}
        </div>
      </div>
    </div>
  );
}

export default MainSettings;
